/**
 * Custom-field key registry — single source of truth for which keys on
 * contacts.custom_fields are owned by the platform vs free for user
 * "Add Custom Field" rows. Mirrors backend/src/domain/constants/customFieldKeys.js
 * — keep both files in sync or the validator will reject what the UI lets
 * through (and vice-versa).
 *
 * Two lists, one purpose:
 *
 *   RESERVED_CUSTOM_FIELD_KEYS — keys we BLOCK in the "Add Custom Field" UI
 *   because a dedicated input above already writes them. Adding them in the
 *   custom rows would silently fight with the dedicated input on submit.
 *
 *   SYSTEM_CUSTOM_FIELD_KEYS — superset of the above PLUS read-only legacy
 *   keys that LeadDetailView already renders as labeled InfoFields (title,
 *   website, address, etc.). Used by the edit-mode loader to filter out
 *   "system" keys before populating the user-editable rows, and by the
 *   detail view's "Other Custom Fields" section to skip the keys it
 *   already renders elsewhere.
 *
 * Why the separation: a contact in this DB can legitimately carry
 * `lead_source: "Meta Lead Ad"` (set by FacebookService) and
 * `campaign_name` (also set by FacebookService) — those must survive an
 * edit round-trip but they shouldn't appear in the user-editable rows.
 */

export const RESERVED_CUSTOM_FIELD_KEYS = new Set<string>([
  // Already-editable via dedicated inputs in ContactForm — adding them in
  // custom rows would race with the dedicated input on submit.
  'source',
  'company',
  'owner',
  'deal_size',
  'privacy',
  'email',
  // Already-displayed legacy keys with dedicated inputs (Lead form fields).
  'title',
  'website',
  'address',
  'city',
  'state',
  'country',
  'zipcode',
  'reference_by',
  'sales_by',
  // Set by FacebookService on Meta Lead Ads (read-only — never user-edited).
  'lead_source',
  'campaign_name',
  'campaign_id',
  'ad_name',
  'ad_id',
  'adset_name',
  'adset_id',
  'form_id',
  // Added 2026-07-30 — the leadgen webhook now persists these instead of
  // discarding them. Same class as the keys above: Meta system data, never
  // user-edited. Mirror: backend/src/domain/constants/customFieldKeys.js.
  'form_name',
  'page_id',
  'page_name',
  'platform',
  // Demo scheduling — has a dedicated UI in LeadDetailView / KanbanCard.
  'demo_scheduled_at',
  // Meta CAPI CRM diagnostic (spec §5). Enum-constrained — see
  // JUNK_REASON_VALUES. Backend mirror: backend/src/domain/constants/customFieldKeys.js.
  'junk_reason',
  // Internal automation bookkeeping — stamped on contact.custom_fields by the
  // flow engine (FlowRunnerService / TriggerDispatcher / ContactUseCases), NOT
  // user data. Must never surface as an editable row or in the read-only
  // "Custom Fields" section. Regression: these leaked into the detail-drawer
  // Custom Fields section on Leads / Customers / Board (all three share
  // LeadDetailView). Pattern-matched families (e.g. any future last_trigger_*)
  // are additionally caught by INTERNAL_CUSTOM_FIELD_KEY_PATTERNS below.
  'is_flows_paused',
  'flows_paused',
  'last_trigger_keyword',
  'last_trigger_at',
])

/**
 * Allowed values for the reserved `junk_reason` key (Meta CAPI CRM spec §5).
 * Backend mirror: backend/src/domain/constants/customFieldKeys.js JUNK_REASON_VALUES.
 * The label map is UI-facing copy for a future Junk-reason picker.
 */
export const JUNK_REASON_VALUES = [
  'invalid_number',
  'wrong_person',
  'language_mismatch',
  'no_answer_after_N',
  'not_interested',
  'duplicate',
] as const

export type JunkReason = (typeof JUNK_REASON_VALUES)[number]

export const JUNK_REASON_LABELS: Record<JunkReason, string> = {
  invalid_number: 'Invalid number',
  wrong_person: 'Wrong person',
  language_mismatch: 'Language mismatch',
  no_answer_after_N: 'No answer (after N attempts)',
  not_interested: 'Not interested',
  duplicate: 'Duplicate',
}

/**
 * Prefix/shape patterns for internal automation keys we don't want to hard-code
 * one-by-one. Anything matching is treated as a system key everywhere (hidden
 * from display + the edit form, preserved in the system bucket on save). Keep
 * these conservative — they must never match a plausible user-chosen field
 * name. `last_trigger_*` and `*flows_paused` are backend-only namespaces.
 */
export const INTERNAL_CUSTOM_FIELD_KEY_PATTERNS: readonly RegExp[] = [
  /^last_trigger_/i,
  /^is_flows_paused$/i,
  /^flows_paused$/i,
]

/**
 * Single source of truth for "should this custom-field key be hidden from the
 * user (display + edit) and preserved as system data on save?". Checks the
 * explicit SYSTEM set first, then the internal-automation patterns. ALL read
 * sites (detail drawer, edit form, splitCustomFields, orphan disclosure) go
 * through this so a key can never be hidden in one place but leak in another.
 */
export function isSystemCustomFieldKey(key: string): boolean {
  if (!key) return false
  if (SYSTEM_CUSTOM_FIELD_KEYS.has(key)) return true
  return INTERNAL_CUSTOM_FIELD_KEY_PATTERNS.some((re) => re.test(key))
}

// SYSTEM_CUSTOM_FIELD_KEYS is currently identical to RESERVED. We keep them
// as separate exports because "reserved" is a write-side concept (blocked in
// the add-custom-field UI) and "system" is a read-side concept (hidden from
// the user-editable list / detail-view custom section). If a future key ever
// needs to be "displayed somewhere else but allowed as a custom field" the
// two lists diverge — keep them so any future divergence is one-line.
export const SYSTEM_CUSTOM_FIELD_KEYS = RESERVED_CUSTOM_FIELD_KEYS

/**
 * Meta Lead Ad system fields — auto-populated by FacebookService when a lead
 * comes in from a Meta lead-ad form. Read-only everywhere in the UI (a user
 * editing `form_id` makes no sense). Ordered for display. The keys are a
 * subset of SYSTEM_CUSTOM_FIELD_KEYS, so they're already excluded from the
 * user-editable rows and the orphan disclosure — this list is purely for the
 * dedicated read-only "Lead Source" section.
 */
export const META_LEAD_AD_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'lead_source', label: 'Source' },
  { key: 'campaign_name', label: 'Campaign' },
  { key: 'ad_name', label: 'Ad name' },
  { key: 'ad_id', label: 'Ad ID' },
  { key: 'form_id', label: 'Form ID' },
]

/**
 * Pull the Meta Lead Ad fields that actually have a value out of a contact's
 * custom_fields (string OR object — coerced internally). Returns ordered
 * `{ key, label, value }` entries; empty/null fields are dropped. Returns an
 * empty array when the contact has no Meta-ad data, so callers can hide the
 * whole section.
 */
export function extractMetaLeadAdFields(
  customFields: unknown
): Array<{ key: string; label: string; value: string }> {
  const cf = coerceCustomFields(customFields)
  const out: Array<{ key: string; label: string; value: string }> = []
  for (const { key, label } of META_LEAD_AD_FIELDS) {
    const raw = cf[key]
    if (raw === null || raw === undefined || raw === '') continue
    out.push({ key, label, value: typeof raw === 'string' ? raw : String(raw) })
  }
  return out
}

/**
 * Map a raw `lead_source` marker (set by the various lead-origin integrations)
 * to the matching DEAL INFO "Source" dropdown value. Returns null when the
 * marker isn't recognized so the caller can fall back to "Lead".
 *
 * Each returned value MUST exist as an option in SYSTEM_LEAD_FIELDS['source']
 * (leadFormFields.ts) or the dropdown renders an empty selection.
 *
 * Backend markers that should land here (set at lead creation / conversion):
 *   - "Meta Lead Ad"  → "Meta Ads"   (FacebookService — already set)
 *   - "Instagram" / "Instagram Lead" / channel "instagram" → "Instagram"
 *   - "Google Ads" / "google_ads"                          → "Google Ads"
 *   - "Scheduling" / "Booking" / "Appointment"             → "Scheduling"
 */
export function mapLeadSourceToOption(rawLeadSource: unknown): string | null {
  if (typeof rawLeadSource !== 'string') return null
  const s = rawLeadSource.trim().toLowerCase()
  if (s === '') return null
  if (s.includes('google')) return 'Google Ads'
  // Instagram before the Meta/FB catch — an "instagram" marker must never
  // fall through to Meta Ads.
  if (s.includes('instagram') || s === 'ig') return 'Instagram'
  // Meta LEAD ADS markers ("Meta Lead Ad", "fb lead", generic "meta") → Meta Ads.
  // A plain "facebook" (set by FacebookService when a Messenger flow lead
  // completes) is a DIFFERENT channel and must read "Facebook", not "Meta Ads".
  if (s === 'facebook' || s === 'fb' || s === 'messenger') return 'Facebook'
  if (s.includes('meta') || s.includes('fb lead') || s.includes('lead ad')) return 'Meta Ads'
  if (s.includes('facebook')) return 'Facebook'
  if (s.includes('schedul') || s.includes('booking') || s.includes('appointment')) return 'Scheduling'
  return null
}

/**
 * Derive the DEAL INFO "Source" dropdown value for a contact.
 *
 * Precedence: an explicitly-stored `custom_fields.source` always wins (the
 * user picked it). Otherwise we map the integration-set `lead_source` marker
 * to its dropdown option (Meta Ads / Instagram / Scheduling). Everything else
 * → "Lead".
 */
export function deriveSourceDefault(customFields: unknown): string {
  const cf = coerceCustomFields(customFields)
  if (cf.source) return String(cf.source)
  return mapLeadSourceToOption(cf.lead_source) || 'Lead'
}

export const MAX_CUSTOM_FIELDS_PER_CONTACT = 30
export const MAX_CUSTOM_FIELD_KEY_LENGTH = 64
export const MAX_CUSTOM_FIELD_VALUE_LENGTH = 2000
// Multiselect arrays: cap element count (mirrors backend customFieldKeys.js).
export const MAX_CUSTOM_FIELD_ARRAY_LENGTH = 50

// Allowed chars in a custom-field key: letters, digits, spaces, underscores,
// hyphens. Backend mirrors this; client validates pre-submit for fast feedback.
// \p{M} (combining marks) lets Devanagari/Tamil/Arabic field names work.
export const CUSTOM_FIELD_KEY_PATTERN = /^[\p{L}\p{N}\p{M} _-]+$/u

export interface CustomFieldRow {
  id: string
  key: string
  value: string
}

/**
 * Coerce a contact's `custom_fields` into a plain object.
 *
 * The backend (and some RDS Data API read paths — same quirk documented for
 * `business_accounts.lead_form_layout`) can hand back `custom_fields` as a
 * JSON **string** instead of a parsed object. Spreading a string into an
 * object (`{ ...customFields }`) enumerates its CHARACTERS — producing
 * `{0:'{', 1:'"', 2:'l', ...}` — which surfaced as dozens of bogus
 * single-character "orphan fields" in the Edit Lead modal. This helper
 * normalizes both shapes at the boundary so every downstream consumer
 * (orphan computation, splitCustomFields, dedicated-input reads) sees an
 * object. Accepts: object → as-is; JSON-string-of-object → parsed; anything
 * else (null, "null", arrays, unparseable string, primitives) → `{}`.
 */
export function coerceCustomFields(raw: unknown): Record<string, any> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, any>
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (trimmed === '' || trimmed === 'null') return {}
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>
      }
    } catch {
      // Unparseable — fall through to empty object rather than leak chars.
    }
  }
  return {}
}

/**
 * Split a contact's custom_fields object into the user-editable rows
 * (everything NOT in SYSTEM_CUSTOM_FIELD_KEYS) and the system bucket that
 * the edit form preserves silently. Skips null/undefined values.
 */
export function splitCustomFields(
  customFields: Record<string, any> | null | undefined
): { systemBucket: Record<string, any>; userRows: CustomFieldRow[] } {
  const systemBucket: Record<string, any> = {}
  const userRows: CustomFieldRow[] = []
  const normalized = coerceCustomFields(customFields)
  let i = 0
  for (const [key, rawValue] of Object.entries(normalized)) {
    if (rawValue === null || rawValue === undefined) continue
    if (isSystemCustomFieldKey(key)) {
      systemBucket[key] = rawValue
    } else {
      userRows.push({
        id: `cf-${Date.now()}-${i++}`,
        key,
        value: typeof rawValue === 'string' ? rawValue : String(rawValue),
      })
    }
  }
  return { systemBucket, userRows }
}

export interface CustomFieldValidationResult {
  ok: boolean
  errors: Record<string, string> // rowId → message
  cleanedRows: CustomFieldRow[]   // trimmed, empty-value rows removed
}

/**
 * Pure validator. Returns per-row errors (keyed by row id) AND a cleaned
 * version of the rows safe to serialize. Empty-value rows are dropped on
 * submit (not an error — just skipped). Empty-key rows ARE errors.
 */
export function validateCustomFieldRows(
  rows: CustomFieldRow[]
): CustomFieldValidationResult {
  const errors: Record<string, string> = {}
  const cleanedRows: CustomFieldRow[] = []
  const seenKeys = new Set<string>()

  for (const row of rows) {
    const trimmedKey = (row.key || '').trim()
    const trimmedValue = (row.value || '').trim()

    // Empty row (both fields blank) — skip silently, allow user to leave it.
    if (trimmedKey === '' && trimmedValue === '') continue

    // Empty key but non-empty value — error.
    if (trimmedKey === '') {
      errors[row.id] = 'Field name is required'
      continue
    }

    if (trimmedKey.length > MAX_CUSTOM_FIELD_KEY_LENGTH) {
      errors[row.id] = `Field name must be ${MAX_CUSTOM_FIELD_KEY_LENGTH} characters or fewer`
      continue
    }

    if (!CUSTOM_FIELD_KEY_PATTERN.test(trimmedKey)) {
      errors[row.id] = 'Field name may only contain letters, digits, spaces, underscores, hyphens'
      continue
    }

    // Case-insensitive reserved-key check — `Source` and `source` both blocked.
    if (RESERVED_CUSTOM_FIELD_KEYS.has(trimmedKey.toLowerCase())) {
      errors[row.id] = `"${trimmedKey}" is a system field — edit it above instead`
      continue
    }

    // Case-insensitive duplicate check inside this form.
    const dupKey = trimmedKey.toLowerCase()
    if (seenKeys.has(dupKey)) {
      errors[row.id] = `Duplicate field name "${trimmedKey}"`
      continue
    }
    seenKeys.add(dupKey)

    if (trimmedValue.length > MAX_CUSTOM_FIELD_VALUE_LENGTH) {
      errors[row.id] = `Value must be ${MAX_CUSTOM_FIELD_VALUE_LENGTH} characters or fewer`
      continue
    }

    // Empty value with non-empty key — drop on submit (don't store keys
    // with blank values), but allow it during editing so the row doesn't
    // vanish under the user's cursor.
    if (trimmedValue === '') continue

    cleanedRows.push({ id: row.id, key: trimmedKey, value: trimmedValue })
  }

  return { ok: Object.keys(errors).length === 0, errors, cleanedRows }
}

/**
 * Serialize cleaned rows to the Record<string, string> shape stored in
 * contacts.custom_fields. Caller should run validateCustomFieldRows first
 * and only call this on the `cleanedRows` output.
 */
export function rowsToRecord(rows: CustomFieldRow[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const row of rows) out[row.key] = row.value
  return out
}
